import ProLayout from '@ant-design/pro-layout';
import { MoreOutlined } from '@ant-design/icons';
import { useHistory, useLocation, withRouter, type RouteComponentProps } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import { Avatar, message } from 'antd';
import { Dropdown, Modal } from 'antd-v5';
import { ajax } from '@/api/ajax';
import { useBoolean } from 'ahooks';
import styles from './index.module.less';
import { useTranslation } from '@/hooks/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import type { ITab } from '@/types';
import { omit } from 'lodash';
import KeepAliveTabs from '../KeepAliveTabs';
import type { IRoute } from '@/types/router';
import ChooseLangDrawer from '../ChooseLangDrawer';
import GlobalOutlinedIcon from './assets/globalOutlined.svg';
import { menusData } from '@/router';

const HOME_PAGE = '/afs-sip/repair-info-search';
const HomeHeader: React.FC<RouteComponentProps> = props => {
  const [activeTab, setActiveTab] = useState(HOME_PAGE);
  const [pageTabs, setTabs] = useState<ITab[]>([]);
  const pageTabsRef = React.useRef(pageTabs);
  const [tabsDict, setTabsDict] = useState<Record<string, any>>({});
  const history = useHistory();
  const location = useLocation();

  const $ = useTranslation();

  const globalMenusData = useMemo(
    () => menusData.map(menu => ({ ...menu, menuName: $(menu.menuName) })),
    [$]
  );

  const currentUser: any = useSelector<RootState>(
    state => state.UserModel.currentUser,
    shallowEqual
  );

  const [drawerVisible, { setTrue: showDrawer, setFalse: hideDrawer }] = useBoolean(false);

  useEffect(() => {
    const [pathname] = activeTab.split('?');
    setActiveTab(`${pathname}`);
  }, [activeTab]);

  const setPageTabs = (tab: ITab) => {
    let key = tab.key;
    tab.path = tab.path || key;
    const [pathname] = key.split('?');
    const menu = globalMenusData.find(item => {
      if (item.url && (pathname.endsWith(item.url) || pathname === item.url || item.url === key)) {
        return true;
      }
      if (/:/.test(item.url)) {
        const pathList = pathname.split('/');
        return item.url
          .split('/')
          .every((path: string, index: number) => /^:/.test(path) || path === pathList[index]);
      }
    });
    console.info('menu:', menu);
    if (!menu && key !== '404') {
      return;
    }
    key = pathname;
    tab.key = key;
    if (!tab.title) {
      const title = pageTabs?.find(item => item.key === key)?.title;
      tab.title = title || menu?.menuName || '';
    }
    if (!tabsDict[key]) {
      //保证唯一性
      const newPageTabs = pageTabs.concat(tab);
      setTabs(newPageTabs);
      pageTabsRef.current = newPageTabs;
      setTabsDict({ ...tabsDict, [key]: tab });
    } else {
      const index = pageTabs.findIndex(item => tab.key === item.key);
      const newPageTags = [...pageTabs];
      const newTab = { ...pageTabs[index], ...tab };
      newPageTags.splice(index, 1, newTab);
      setTabs(newPageTags);
      pageTabsRef.current = newPageTags;
      setTabsDict({ ...tabsDict, [key]: newTab });
    }
    setActiveTab(key);
    document.title = `${tab.title} - SIP`;
  };
  const removeTabs = (key: string) => {
    const nextTabs = pageTabs.filter(item => item.key !== key);
    const nextTabsDict = omit(tabsDict, key);
    setTabsDict(nextTabsDict);
    setTabs(nextTabs);
    pageTabsRef.current = nextTabs;
    if (!nextTabs.length) {
      history.push(`${HOME_PAGE}`);
      return;
    }

    if (activeTab === ' ') {
      const dict = nextTabs[nextTabs.length - 1];
      history.push(`${dict?.path || dict?.key || HOME_PAGE}`);
      return;
    }

    // 删掉的Tab是当前活动的Tab时
    if (key === activeTab) {
      const nextKey = nextTabs[nextTabs.length - 1]
        ? nextTabs[nextTabs.length - 1].path || nextTabs[nextTabs.length - 1].key
        : '/';
      // setActiveTab(nextKey)
      history.push(`${nextKey}`);
    }

    // NOTE: 这个事件主要为了兼容骁龙子应用的关闭Tab逻辑
    window.postMessage({ msgType: 'closeTab', path: key });
  };

  useEffect(() => {
    const callback = (evt: MessageEvent) => {
      if (evt.data?.payload?.event === 'closeTab') {
        console.info('tab:', evt.data?.payload?.key || activeTab);
        removeTabs(evt.data?.payload?.key || activeTab);
      } else if (evt.data?.payload?.event === 'openNewTab') {
        let key = evt.data?.payload?.tab.key;
        const [pathname, search] = key.split('?');
        const searchParams = new URLSearchParams(search);
        const newSearch = searchParams.toString();
        key = `${pathname}${newSearch ? '?' + newSearch : ''}`;
        setPageTabs(evt.data?.payload?.tab);
        if (/should_refresh=/.test(key)) {
          history.replace(`${key}`);
        } else {
          history.push(`${key}`);
        }
      } else if (evt.data?.payload?.event === 'updateTab') {
        setPageTabs(evt.data?.payload?.tab);
      } else if (evt.data?.payload?.event === 'closeAllTab') {
        pageTabs.forEach(tab => {
          window.postMessage({ msgType: 'closeTab', path: tab.key });
        });
        setTabs([]);
        pageTabsRef.current = [];
        history.replace(`${HOME_PAGE}`);
      }
    };
    window.addEventListener('message', callback);
    return () => window.removeEventListener('message', callback);
  });

  const initHeaderAndTabsState = () => {
    const { pathname, search } = location;
    if (pathname === '/') {
      history.push(`${HOME_PAGE}`);
      return;
    }
    let key = '';
    let path = '';
    const paths = pathname.split('/');
    key = `${paths.join('/')}`;
    path = `${key}${search}`;
    const menu = globalMenusData.find((item: IRoute) => {
      if (item.url && (key.endsWith(item.url) || key === item.url || item.url === key)) {
        return true;
      }
      if (/:/.test(item.url)) {
        const pathList = key.split('/');
        return item.url
          .split('/')
          .every(
            (itemPath: string, index: number) => /^:/.test(itemPath) || itemPath === pathList[index]
          );
      }
    });
    console.info('key:', key);
    console.info('menu:', menu);
    if (menu) {
      let title: string | undefined = '';
      // if (menu.url === pathname) {
      title = pageTabsRef.current?.find(item => item.key === key)?.title;
      // }
      setPageTabs({
        key,
        title: title || menu.menuName || 'untitled',
        path,
        search
      });
      setActiveTab(key);
    } else {
      if (![HOME_PAGE, '/404', '/'].includes(key)) {
        if (window.history.length > 1) {
          const pagekey = '404';
          const pagePath = `/404`;
          history.push(pagePath);
          setPageTabs({
            key: pagekey,
            title: $('NotFound'), // 修改: 使用 $("NotFound") 引入翻译文本
            path: pagePath
          });
          setActiveTab(pagekey);
        } else {
          // 回到首页
          history.push(`${HOME_PAGE}`);
        }
      } else if ('/404'.includes(key)) {
        const pagekey = '404';
        const pagePath = `/404`;
        setPageTabs({
          key: pagekey,
          title: $('NotFound'), // 修改: 使用 $("NotFound") 引入翻译文本
          path: pagePath
        });
        setActiveTab(pagekey);
      } else {
        setActiveTab(HOME_PAGE);
      }
    }
  };

  useEffect(() => {
    initHeaderAndTabsState();
  }, [globalMenusData.length, location.pathname, location.search]);

  return (
    <ProLayout
      title={`${$('title')}`}
      logo='https://xps01.xiaopeng.com/www/public/img/black-logo.98ed887d.svg'
      layout='top'
      navTheme='light'
      rightContentRender={() => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '48px' }}>
            <img
              style={{ marginRight: 27, fontSize: 20, cursor: 'pointer' }}
              onClick={e => {
                e.stopPropagation();
                showDrawer();
              }}
              src={GlobalOutlinedIcon}
            />
            <ChooseLangDrawer drawerVisible={drawerVisible} hideDrawer={hideDrawer} />
            {currentUser.headImg && (
              <Avatar src={currentUser.headImg} style={{ backgroundColor: 'white' }} />
            )}
            <span style={{ margin: '0 20px 0 5px' }}>{currentUser.nickName}</span>
            <Dropdown
              placement='bottomLeft'
              menu={{
                items: [
                  {
                    label: $('logout'), // 修改: 使用 $() 引入翻译文本
                    key: 'logout',
                    onClick: () => {
                      Modal.confirm({
                        title: $('logout'), // 修改: 使用 $() 引入翻译文本
                        content: $('are_you_sure_to_logout'), // 新增: 添加确认退出登录的翻译文本
                        centered: true,
                        onOk: () => {
                          ajax({
                            url: '/webAuthSdk/checkLoginStatus',
                            method: 'post'
                          })
                            .then(res => {
                              if (res.code === 200) {
                                window.location.href = res.data.logoutUrl;
                              } else {
                                message.error(res.msg);
                              }
                            })
                            .catch(err => {
                              message.error(err.message);
                            });
                        }
                      });
                    }
                  }
                ]
              }}
            >
              <MoreOutlined style={{ cursor: 'pointer' }} />
            </Dropdown>
          </div>
        );
      }}
      fixedHeader={true}
    >
      <KeepAliveTabs
        activeTab={activeTab}
        tabs={pageTabs}
        tabsDict={tabsDict}
        removeTabs={removeTabs}
      />
      <div className={styles.contentRoot}>{props.children}</div>
    </ProLayout>
  );
};

const withRouterHome = withRouter(HomeHeader);

export default withRouterHome;
